import { useQuery } from "@tanstack/react-query";
import {
  CatchBoundary,
  useNavigate,
  useRouteContext,
  useRouterState,
  useSearch,
} from "@tanstack/react-router";
import { Suspense, useDeferredValue, useEffect, useState } from "react";
import { PageFailed } from "@/client/components/route-states";
import { InSideSlot } from "@/client/components/side-slot";
import { TaskPanelSkeleton } from "@/client/components/task-panel-skeleton";
import { isTyping } from "@/client/lib/keyboard/shortcut-handler";
import { lazyComponent } from "@/client/lib/lazy-component";
import { useNow } from "@/client/lib/use-now";
import { usePhone } from "@/client/lib/use-phone";
import { dayIn } from "@/features/attendance/rules";
import { peopleQuery } from "@/features/people/people-query";
import { commentsQuery } from "@/features/tasks/comments-query";
import type { TaskPanelProps } from "@/features/tasks/components/task-panel";
import { taskActivityQuery, taskQuery } from "@/features/tasks/task-query";
import { focusTaskRow } from "@/features/tasks/use-task-keys";

const MINUTE = 60_000;
// As long as the router keeps an old screen before drawing the next one's pending state.
const HOLD_MS = 1000;

export const TASK_PANEL_TITLE_ID = "task-panel-title";

// The panel's pickers, comments and repeat editor stay out of the shell, which every screen's first
// load carries (`scripts/client-budget.ts`).
export const taskPanel = lazyComponent(() =>
  import("@/features/tasks/components/task-panel").then((m) => m.TaskPanel),
);

/** The task named by `?task=` beside the screen it was opened from, with the company's people. */
export function TaskPanelSlot() {
  const { task: asked } = useSearch({ from: "/_authenticated/$slug" });
  // Drawn just after the press that opens, closes or changes it has painted: the panel's pickers
  // are a long render, and the row answers without them.
  const next = useDeferredValue(asked);
  const task = useShownTask(next);
  const { email, role, companyForedayOn, companyTimezone } = useRouteContext({
    from: "/_authenticated/$slug",
  });
  const navigate = useNavigate();
  const phone = usePhone();
  const now = useNow(MINUTE);
  const { data: rows = [] } = useQuery(peopleQuery);
  // Asked for and not yet drawn, the panel has no Escape of its own (`InSideSlot`): the press it
  // would answer once drawn closes it now.
  const waiting = !phone && task === undefined ? asked : undefined;
  useEffect(() => {
    if (waiting === undefined) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented) return;
      if (isTyping(event.target)) return;
      void navigate({
        to: ".",
        search: (prev: object) => ({ ...prev, task: undefined }),
      });
      focusTaskRow(waiting);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [waiting, navigate]);
  if (!task) return null;

  // A deactivated person is never given a task, so the picker leaves them out.
  const people = rows.filter(
    (row) => row.kind === "person" && row.state === "active",
  );
  const authors = rows.filter((row) => row.kind === "person");
  const me = rows.find((row) => row.kind === "person" && row.email === email);

  const close = () =>
    void navigate({
      to: ".",
      search: (prev: object) => ({ ...prev, task: undefined }),
    });
  // Back on the row of the task last asked for, which the arrows may have stepped to from the opener.
  const closeToRow = () => {
    close();
    focusTaskRow(next ?? task);
  };
  const panel = (
    <PanelShown
      key={task}
      taskId={task}
      people={people}
      authors={authors}
      meId={me?.id ?? null}
      role={role}
      foredayOn={companyForedayOn === true}
      today={dayIn(companyTimezone, now)}
      timeZone={companyTimezone}
      onClose={phone ? close : closeToRow}
    />
  );

  // On a phone the panel takes the screen's place in the shell.
  if (phone) {
    return (
      <aside
        aria-labelledby={TASK_PANEL_TITLE_ID}
        className="flex min-h-0 flex-1 flex-col bg-linear-to-b from-secondary-from to-secondary-to"
      >
        {panel}
      </aside>
    );
  }
  return (
    <InSideSlot labelledBy={TASK_PANEL_TITLE_ID} onClose={closeToRow}>
      {panel}
    </InSideSlot>
  );
}

/**
 * Moving from one task to another, the last one stays drawn until the next one's reads have
 * answered (or for `HOLD_MS`), so quick clicks between rows never flash the panel's skeletons. A
 * task read before answers from the cache at once.
 */
function useShownTask(asked: string | undefined): string | undefined {
  const [shown, setShown] = useState(asked);
  const [waited, setWaited] = useState<string | undefined>();
  const id = asked ?? "";
  const enabled = asked !== undefined;
  const reads = [
    useQuery({ ...taskQuery(id), enabled }),
    useQuery({ ...commentsQuery(id), enabled }),
    useQuery({ ...taskActivityQuery(id), enabled }),
  ];
  const holding = shown !== undefined && asked !== undefined && shown !== asked;
  useEffect(() => {
    if (!holding) return;
    const timer = setTimeout(() => setWaited(asked), HOLD_MS);
    return () => clearTimeout(timer);
  }, [holding, asked]);
  const answered = reads.every((read) => !read.isPending);
  if (shown !== asked && (!holding || answered || waited === asked)) {
    setShown(asked);
    setWaited(undefined);
  }
  return shown;
}

// A failed load or render shows in the panel, not over the screen; Try again re-runs the loaders,
// and the new load time resets the boundary.
function PanelShown(props: TaskPanelProps) {
  const loadedAt = useRouterState({
    select: (state) => state.matches.at(-1)?.updatedAt,
  });
  return (
    <CatchBoundary
      getResetKey={() => loadedAt ?? 0}
      errorComponent={PageFailed}
    >
      <Picked {...props} />
    </CatchBoundary>
  );
}

// Picked above the Suspense and once per task, so a panel whose code has arrived never suspends.
// Until it has, the column shows the panel's loading frame rather than standing empty.
function Picked(props: TaskPanelProps) {
  const [Panel] = useState(taskPanel.pick);
  return (
    <Suspense fallback={<TaskPanelSkeleton />}>
      <Panel {...props} />
    </Suspense>
  );
}
